"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InvoiceUpdateTransport = exports.InvoiceCreateTransport = exports.InvoiceTransport = exports.isVoided = exports.TermExtendableInvoiceStatuses = exports.VoidedInvoiceStatuses = exports.INVOICE_STATUS = exports.InvoiceStatus = exports.InvoiceStatusLabels = exports.InvoiceStatuses = void 0;
const typebox_1 = require("@sinclair/typebox");
const common_1 = require("../../common");
const invoice_transport_1 = require("../invoice-transport");
const invoice_reference_transport_1 = require("./invoice-reference-transport");
const line_item_transport_1 = require("./line-item-transport");
const payment_transport_1 = require("./payment-transport");
const refund_transport_1 = require("./refund-transport");
/**
 * v2 owns its status vocabulary. v1 derives its status from the `void` boolean and cannot express
 * `MERGED`, so the two lists are deliberately separate rather than shared.
 */
exports.InvoiceStatuses = [
    'DRAFT',
    'ISSUED',
    'PAID',
    'VOID',
    'MERGED',
    'PARTIALLY_PAID',
    'REFUNDED',
    'PAST_DUE',
    'DELETED',
];
exports.InvoiceStatusLabels = {
    DRAFT: 'Draft',
    ISSUED: 'Issued',
    PAID: 'Paid',
    VOID: 'Void',
    MERGED: 'Merged',
    PARTIALLY_PAID: 'Partially Paid',
    REFUNDED: 'Refunded',
    PAST_DUE: 'Past Due',
    DELETED: 'Discarded',
};
exports.InvoiceStatus = typebox_1.Type.Union(exports.InvoiceStatuses.map((status) => typebox_1.Type.Literal(status)));
/** Named-constant access, e.g. `INVOICE_STATUS.DRAFT`. Derived from `InvoiceStatuses` so it can't drift. */
exports.INVOICE_STATUS = Object.fromEntries(exports.InvoiceStatuses.map((status) => [status, status]));
exports.VoidedInvoiceStatuses = ['VOID', 'MERGED', 'DELETED'];
exports.TermExtendableInvoiceStatuses = ['ISSUED', 'PARTIALLY_PAID', 'PAST_DUE'];
const VOIDED_INVOICE_STATUS_SET = new Set(exports.VoidedInvoiceStatuses);
/**
 * An invoice that will never be collected, whatever the reason: voided outright, deleted before it
 * was ever issued, or merged.
 */
const isVoided = (status) => status !== undefined && VOIDED_INVOICE_STATUS_SET.has(status);
exports.isVoided = isVoided;
exports.InvoiceTransport = typebox_1.Type.Object({
    id: typebox_1.Type.String({ format: 'uuid' }),
    haulerID: typebox_1.Type.String({ format: 'uuid' }),
    invoiceNumber: typebox_1.Type.String(),
    references: typebox_1.Type.Array(invoice_reference_transport_1.InvoiceReferenceTransport),
    status: typebox_1.Type.Optional(exports.InvoiceStatus),
    reviewStatus: typebox_1.Type.Optional(invoice_transport_1.ReviewStatus),
    /** When the invoice was voided. `status` says *that* it is void; this says when. */
    voidDate: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'date-time' })),
    lineItems: typebox_1.Type.Array(line_item_transport_1.LineItemTransport),
    subTotal: typebox_1.Type.Integer(),
    tax: typebox_1.Type.Integer(),
    remainingBalance: typebox_1.Type.Integer(),
    total: typebox_1.Type.Integer(),
    payments: typebox_1.Type.Array(payment_transport_1.PaymentTransport),
    refunds: typebox_1.Type.Array(refund_transport_1.RefundTransport),
    issueDate: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'date-time' })),
    /** Payment term in days. */
    term: typebox_1.Type.Integer({ minimum: 0 }),
    /** Derived: `issueDate` + `term`. */
    dueDate: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'date-time' })),
    memo: typebox_1.Type.Optional(typebox_1.Type.String()),
    internalNotes: typebox_1.Type.Optional(typebox_1.Type.String()),
    metadata: typebox_1.Type.Optional(common_1.MetaData),
});
/**
 * Invoice as written on create. Everything derived (`total`, `remainingBalance`, `dueDate`) or
 * server-owned (`id`, `haulerID` from the token, `metadata`) is absent by construction rather than
 * accepted and ignored.
 *
 * `status` defaults to `DRAFT`. `ISSUED` requires an `issueDate` of today or earlier and issues the
 * invoice at create. A `DRAFT` with an `issueDate` is scheduled: it is issued automatically once that
 * date arrives.
 */
exports.InvoiceCreateTransport = typebox_1.Type.Object({
    status: typebox_1.Type.Optional(typebox_1.Type.Union([typebox_1.Type.Literal('DRAFT'), typebox_1.Type.Literal('ISSUED')])),
    issueDate: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'date-time' })),
    term: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0 })),
    memo: typebox_1.Type.Optional(typebox_1.Type.String()),
    internalNotes: typebox_1.Type.Optional(typebox_1.Type.String()),
    invoiceNumber: typebox_1.Type.Optional(typebox_1.Type.String()),
    references: typebox_1.Type.Optional(typebox_1.Type.Array(invoice_reference_transport_1.InvoiceReferenceTransport)),
    lineItems: typebox_1.Type.Optional(typebox_1.Type.Array(line_item_transport_1.InvoiceLineItemCreateTransport)),
});
/**
 * Invoice as written on update. `syncedWithAccounting` is server-owned (the caller doesn't know it)
 * and so is absent. `lineItems` / `references`, when present, replace the existing collection
 * wholesale.
 */
exports.InvoiceUpdateTransport = typebox_1.Type.Object({
    status: typebox_1.Type.Optional(exports.InvoiceStatus),
    issueDate: typebox_1.Type.Optional((0, common_1.Nullable)(typebox_1.Type.String({ format: 'date-time' }))),
    term: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0 })),
    memo: typebox_1.Type.Optional((0, common_1.Nullable)(typebox_1.Type.String())),
    internalNotes: typebox_1.Type.Optional((0, common_1.Nullable)(typebox_1.Type.String())),
    reviewStatus: typebox_1.Type.Optional(invoice_transport_1.ReviewStatus),
});
//# sourceMappingURL=invoice-transport.js.map